import {zodResolver} from "@hookform/resolvers/zod";
import {type VerifyOtpState} from "@src/features/auth/auth-routes";
import {useSendPhoneOtpMutation} from "@src/features/auth/redux/auth-api";
import {Button} from "@src/ui/views/button";
import {Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage} from "@src/ui/views/form";
import {PhoneInput} from "@src/ui/views/phone-input";
import * as React from "react";
import {useForm} from "react-hook-form";
import * as RPNInput from "react-phone-number-input";
import {isValidPhoneNumber} from "react-phone-number-input";
import {type NavigateOptions, useNavigate} from "react-router-dom";
import {z} from "zod";


const FormSchema = z.object({
	phoneNumberInput: z.string()
		.refine(isValidPhoneNumber, {message: "Invalid phone number"})
});


type FormData = z.infer<typeof FormSchema>;


export function LoginScreen() {
	const navigate = useNavigate();
	
	const [triggerSendPhoneOtp, sendPhoneOtpState] = useSendPhoneOtpMutation();
	
	const onSubmit = React.useCallback(async (data: FormData) => {
		const submittedPhoneNumber = data.phoneNumberInput;
		await triggerSendPhoneOtp({phoneNumber: submittedPhoneNumber}).unwrap();
		const state: VerifyOtpState = {
			phoneNumber: submittedPhoneNumber
		} satisfies VerifyOtpState;
		const options: NavigateOptions = {
			state: state
		} satisfies NavigateOptions;
		navigate("/auth/verifyOtp", options);
	}, [triggerSendPhoneOtp]);
	
	const form = useForm<FormData>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			phoneNumberInput: ""
		}
	});
	
	const canSubmit = form.formState.isValid
		&& !form.formState.isSubmitting
		&& !sendPhoneOtpState.isLoading;
	
	return (
		<div className="flex w-full h-max flex-row items-center justify-center">
			<div className="flex flex-col justify-center p-4">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="space-y-8 flex flex-col items-start"
						key="PhoneNumberForm">
						<FormDescription className="text-left text-xl text-foreground">
							Log In with a Verification Code
						</FormDescription>
						<FormField
							control={form.control}
							name="phoneNumberInput"
							disabled={form.formState.isSubmitting || sendPhoneOtpState.isLoading}
							render={({field}) => (
								<FormItem className="flex flex-col items-start">
									<FormLabel className="text-left">Phone Number</FormLabel>
									<FormControl className="w-full">
										<PhoneInput
											{...field}
											defaultCountry="IL"
											placeholder="Enter your phone number"
											value={field.value ? field.value as RPNInput.Value : undefined}
										/>
									</FormControl>
									<FormMessage/>
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							variant="default"
							size="default"
							className="bg-blue-500 text-white p-2 w-full"
							disabled={!canSubmit}>
							{sendPhoneOtpState.isLoading ? "Sending..." : "Send Verification Code"}
						</Button>
					</form>
				</Form>
				{
					sendPhoneOtpState.isError && (
						<p className="text-red-500">
							Failed to send OTP. Please try again.
						</p>
					)
				}
			</div>
		</div>
	);
}
